import { articlesBasics } from '@/mocks/articlesBasics';
import { articlesBasicsRisk } from '@/mocks/articlesBasicsRisk';
import { articlesTrading } from '@/mocks/articlesTrading';
import { articlesTradingPractice } from '@/mocks/articlesTradingPractice';
import { articlesTradingDiscipline } from '@/mocks/articlesTradingDiscipline';
import { articlesPlatform } from '@/mocks/articlesPlatform';
import { articlesAnalysis } from '@/mocks/articlesAnalysis';
import { articlesAnalysisExtra } from '@/mocks/articlesAnalysisExtra';
import { articlesAnalysisStructure } from '@/mocks/articlesAnalysisStructure';
import { articlesAnalysisLevels } from '@/mocks/articlesAnalysisLevels';
import { articlesAnalysisPatterns } from '@/mocks/articlesAnalysisPatterns';
import { articlesRisk } from '@/mocks/articlesRisk';
import { articlesRiskExtra } from '@/mocks/articlesRiskExtra';
import { categories } from '@/mocks/categories';
import { tutorialArticles } from '@/utils/mt4mt5Tutorials';
import type { Article } from '@/utils/articleTypes';

export const allArticles: Article[] = [
  ...articlesBasics,
  ...articlesBasicsRisk,
  ...articlesTrading,
  ...articlesTradingPractice,
  ...articlesTradingDiscipline,
  ...articlesPlatform,
  ...articlesAnalysis,
  ...articlesAnalysisExtra,
  ...articlesAnalysisStructure,
  ...articlesAnalysisLevels,
  ...articlesAnalysisPatterns,
  ...articlesRisk,
  ...articlesRiskExtra,
  ...tutorialArticles,
];

export type { Article, ArticleSection, ArticleStep } from '@/utils/articleTypes';
export type Category = (typeof categories)[number];

export function articlePath(slug: string) {
  return `/${slug}`;
}

export function getArticleBySlug(slug: string): Article | undefined {
  return allArticles.find((a) => a.slug === slug);
}

/** Returns the article slug if the given path (e.g. "/what-is-forex") maps to a known article, otherwise null. */
export function getArticleSlugFromPath(path: string): string | null {
  const slug = path.replace(/^\//, '').split(/[?#]/)[0].replace(/\/$/, '');
  if (!slug) return null;
  return getArticleBySlug(slug) ? slug : null;
}

export function getCategoryBySlug(slug: string): Category | undefined {
  return categories.find((c) => c.slug === slug);
}

export function getArticlesByCategory(categorySlug: string): Article[] {
  return allArticles.filter((a) => a.categorySlug === categorySlug);
}

export function getRelatedArticles(article: Article): Article[] {
  const related = article.related
    .map((slug) => getArticleBySlug(slug))
    .filter((a): a is Article => Boolean(a));
  if (related.length > 0) return related.slice(0, 3);
  return allArticles.filter((a) => a.categorySlug === article.categorySlug && a.slug !== article.slug).slice(0, 3);
}

export function getCategoryNeighbours(categorySlug: string, slug: string) {
  const list = getArticlesByCategory(categorySlug);
  const index = list.findIndex((a) => a.slug === slug);
  return {
    prev: index > 0 ? list[index - 1] : null,
    next: index >= 0 && index < list.length - 1 ? list[index + 1] : null,
  };
}

export function getLatestArticles(count = 6): Article[] {
  return [...allArticles]
    .sort((a, b) => (a.updatedDate < b.updatedDate ? 1 : -1))
    .slice(0, count);
}

export function formatThaiDate(iso: string) {
  const date = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat('th-TH', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

export function estimateReading(sectionsCount: number) {
  return `${Math.max(4, sectionsCount + 3)} นาที`;
}